import cn from "classnames";

// Mui
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Button,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Table,
    Select,
    MenuItem,
    ListItemText,
    Chip,
    FormLabel,
    Skeleton,
} from "@mui/material";

// Images
import drax_lighthouse_animation from "assets/animations/drax_lighthouse_animation.gif";

// Icons
import icons from "enums/icons";

// Hooks
import { FormattedMessage, useIntl } from "react-intl";
import useAssessmentCentre from "./useAssessmentCentre";

// Config
import { paceData } from "./config";

// Styles
import AssessmentCentreStyles from "./AssessmentCentre.styles";

// Components
import CustomIcon from "components/CustomIcon";
import { Checkbox } from "components/Checkbox";
import { SortMenu } from "components/SortMenu";
import { ClearFilterButton } from "components/Buttons";
import NavButton from "components/HeaderNavigation/NavButton";
import { Header, Body } from "components/Layout/LayoutContent";
import TablePagination from "components/Talent/TablePagination";
import { isEmpty, isEqual } from "lodash";
import { UserTableRow } from "./components";
import { InviteUsersModal } from "pages/HubManagement/components/InviteUsers";

const AssessmentCentre = () => {
    const intl = useIntl();
    const classes = AssessmentCentreStyles();

    const {
        users,
        filters,
        teamsData,
        usersSort,
        tableHeader,
        inputName,
        inputPage,
        paginationTextInputRef,
        disableClearAll,
        filtersInitialValue,
        inviteModalOpen,
        activeTab,
        pendingInvitations,
        orgId,
        disabledReminderButton,
        disableCheckAllUsers,
        areSomeImcompleteUsersChecked,
        areAllImcompleteUsersChecked,
        canInviteUsers,
        handleFetchData,
        handleSort,
        handleChangeFilters,
        handleClearFilters,
        toggleCheckUser,
        toggleCheckAllUsers,
        handlePageChange,
        handleInputChange,
        handleOnBlur,
        handleOnKeyDown,
        handleShowReport,
        handleDownloadReport,
        handleTransferProfile,
        handleGetInfo,
        handleSendReminder,
        handleInviteSelectedUsers,
        handleOpenInvitationModal,
        closeInviteModal,
    } = useAssessmentCentre();

    return (
        <>
            <Header backgroundColor="primary">
                <Box className={classes.headerInner}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography className={classes.headerTitle}>PACE Portal</Typography>
                        <NavButton
                            icon={null}
                            variant="outlined"
                            showBackArrow={false}
                            onClick={() => console.log("clicked")}
                        >
                            Go to User Management
                        </NavButton>
                    </Box>
                </Box>
            </Header>

            <Body>
                <Box className={classes.bodyHeader}>
                    <Typography className={classes.intro}>
                        Here you can view a user’s progress through the assessment, download a PDF of the user’s results
                        and view their report on the platform.
                    </Typography>
                    <Box className={classes.toolbox}>
                        <Box className={classes.filtersWrapper}>
                            <Box sx={{ maxWidth: 300, mr: 1 }}>
                                <TextField
                                    size="small"
                                    id="user-filter"
                                    name="user-filter"
                                    autoComplete="off"
                                    value={inputName}
                                    className={classes.filterByNameInput}
                                    onChange={(event) => handleChangeFilters(event)}
                                    placeholder={intl.formatMessage({
                                        id: "filters.placeholder.user",
                                    })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CustomIcon icon={icons.searchInverted} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                            <Box sx={{ maxWidth: 200, mr: 1 }}>
                                <FormLabel className={classes.label}>PACE</FormLabel>
                                <Select
                                    multiple
                                    id="filter_pace_status"
                                    name="filter_pace_status"
                                    labelId="filter-by-pace-status"
                                    value={filters.filter_pace_status}
                                    className={classes.selectField}
                                    onChange={(event) => handleChangeFilters(event)}
                                    IconComponent={(props) => (
                                        <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                                    )}
                                    renderValue={(selected) => {
                                        const numSelected = selected.length - 1;
                                        const lastSelected = paceData.find((pace) => selected.includes(pace.id));
                                        return (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Typography
                                                    className={cn({
                                                        [classes.filterPlaceholderLabel]: lastSelected?.id === "all",
                                                    })}
                                                >
                                                    {lastSelected?.label}
                                                </Typography>
                                                {numSelected > 0 && <Chip size="small" label={`+${numSelected}`} />}
                                            </Box>
                                        );
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            className: classes.selectFilterPaper,
                                        },
                                    }}
                                >
                                    {paceData.map((pace) => {
                                        return (
                                            <MenuItem
                                                key={pace.id}
                                                value={pace.id}
                                                className={classes.roleSelectMenuItem}
                                            >
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox checked={filters.filter_pace_status.includes(pace.id)} />
                                                    {pace.icon && (
                                                        <CustomIcon
                                                            icon={icons[pace.icon]}
                                                            sx={{ fontSize: 14, mr: 1 }}
                                                        />
                                                    )}
                                                    <ListItemText primary={pace.label} />
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                    <li className={classes.selectMenuClear}>
                                        <ClearFilterButton
                                            isDisabled={filters.filter_pace_status.includes("all")}
                                            onClick={() => handleClearFilters("filter_pace_status")}
                                        />
                                    </li>
                                </Select>
                            </Box>
                            <Box sx={{ maxWidth: 200 }}>
                                <FormLabel className={classes.label}>
                                    {intl.formatMessage({ id: "filters.labels.team" })}
                                </FormLabel>
                                <Select
                                    multiple
                                    id="filter_teams"
                                    name="filter_teams"
                                    labelId="filter-by-team-name"
                                    value={filters.filter_teams}
                                    className={classes.selectField}
                                    onChange={(event) => handleChangeFilters(event)}
                                    IconComponent={(props) => (
                                        <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                                    )}
                                    MenuProps={{
                                        PaperProps: {
                                            className: classes.selectFilterPaper,
                                        },
                                    }}
                                    renderValue={(selected) => {
                                        if (filters.filter_teams.includes("all")) {
                                            return (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Typography className={classes.filterPlaceholderLabel}>
                                                        All (default)
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                        const numSelected = selected.length - 1;
                                        const lastSelected = teamsData.find((team) => team.name === selected.at(-1));
                                        return (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Typography
                                                    className={cn({
                                                        [classes.filterPlaceholderLabel]: lastSelected?.id === "all",
                                                    })}
                                                >
                                                    {lastSelected?.name}
                                                </Typography>
                                                {numSelected > 0 && <Chip size="small" label={`+${numSelected}`} />}
                                            </Box>
                                        );
                                    }}
                                >
                                    <MenuItem key="all" value="all" className={classes.roleSelectMenuItem}>
                                        <Box display="flex" alignItems="center">
                                            <Checkbox checked={filters.filter_teams.includes("all")} />
                                            <ListItemText primary="All (default)" />
                                        </Box>
                                    </MenuItem>

                                    {/* IMPORTANT: Teams API response do not have an id. Using generateID() to set ids after request!!! */}
                                    {teamsData.map((team) => {
                                        return (
                                            <MenuItem
                                                key={team.id}
                                                value={team.name}
                                                className={classes.roleSelectMenuItem}
                                            >
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox checked={filters.filter_teams.includes(team.name)} />
                                                    {team.name === "all" && <ListItemText primary={team.name} />}
                                                    {team.name !== "all" && team.id !== "no-team" && (
                                                        <Chip color="default" label={team.name} />
                                                    )}
                                                    {team.id === "no-team" && (
                                                        <Chip variant="outlined" label={team.name} />
                                                    )}
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                    <li className={classes.selectMenuClear}>
                                        <ClearFilterButton
                                            isDisabled={filters.filter_teams.includes("all")}
                                            onClick={() => handleClearFilters("filter_teams")}
                                        />
                                    </li>
                                </Select>
                            </Box>
                            <Box>
                                <ClearFilterButton
                                    isDisabled={disableClearAll}
                                    onClick={() => handleClearFilters("all")}
                                />
                            </Box>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <NavButton
                                disabled={disabledReminderButton}
                                showBackArrow={false}
                                onClick={() => handleSendReminder()}
                                icon={null}
                            >
                                Send PACE Reminder
                            </NavButton>
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <TablePagination
                            currentPage={users.meta.page_no}
                            totalPages={users.meta.pages}
                            pageValue={inputPage}
                            onPageChange={handlePageChange}
                            handleInputChange={handleInputChange}
                            handleOnBlur={handleOnBlur}
                            handleOnKeyDown={handleOnKeyDown}
                            inputRef={paginationTextInputRef}
                        />
                        {canInviteUsers && (
                            <Box display="flex" alignItems="center">
                                <Button
                                    onClick={handleInviteSelectedUsers}
                                    className={classes.invitationsButton}
                                    variant="text"
                                >
                                    Invite Users /{"  "}
                                </Button>
                                <Button
                                    onClick={handleOpenInvitationModal}
                                    className={cn(classes.invitationsButton, classes.invitationsButton2)}
                                    variant="text"
                                >
                                    Manage Pending Users
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Users */}
                <Box className={classes.usersTable}>
                    <TableContainer>
                        <Table>
                            <TableHead className={classes.tableHeader}>
                                <TableRow>
                                    {tableHeader
                                        .filter((cell) => !cell.isHidden)
                                        .map((cell) => {
                                            return (
                                                <TableCell key={cell.id} sx={cell.sx}>
                                                    {cell.isSortable ? (
                                                        <Box className={classes.cellWithSortButton}>
                                                            <SortMenu
                                                                columnId={cell.id}
                                                                currentSortBy={usersSort}
                                                                sortConfig={cell.sort}
                                                                // disabled={disableAll}
                                                                icon={
                                                                    <CustomIcon
                                                                        className={classes.sortButton}
                                                                        icon={icons.sortArrows}
                                                                    />
                                                                }
                                                                {...(!isEmpty(cell.sort)
                                                                    ? {
                                                                          onSort: (order, columnId) =>
                                                                              handleSort(order, columnId),
                                                                      }
                                                                    : {})}
                                                            />
                                                            <Typography>
                                                                {cell.name}{" "}
                                                                {cell.id === "user" && `(${users.meta.hits})`}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography>{cell.name}</Typography>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    <TableCell sx={{ width: 24 }}>
                                        <Checkbox
                                            size="small"
                                            indeterminate={areSomeImcompleteUsersChecked}
                                            checked={areAllImcompleteUsersChecked}
                                            onChange={toggleCheckAllUsers}
                                            disabled={disableCheckAllUsers}
                                            inputProps={{ "aria-label": "Select all users" }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ width: 120 }}>&nbsp;</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Data */}
                                {users.status === "done" &&
                                    Boolean(users.data.length) &&
                                    users.data.map((user) => (
                                        <UserTableRow
                                            key={user.id}
                                            data={user}
                                            Checkbox={
                                                <Checkbox
                                                    size="small"
                                                    checked={user.checked}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(ev) => toggleCheckUser(ev, user.id)}
                                                    inputProps={{
                                                        "aria-label": "Select user",
                                                    }}
                                                />
                                            }
                                            handleShowReport={handleShowReport}
                                            handleDownloadReport={handleDownloadReport}
                                            handleTransferProfile={handleTransferProfile}
                                            handleGetInfo={handleGetInfo}
                                            handleSendReminder={handleSendReminder}
                                        />
                                    ))}

                                {/* Fetching */}
                                {users.status === "fetching" &&
                                    Array.from(Array(6)).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={7}>
                                                <Skeleton variant="rounded" animation="wave" />
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                {/* No data */}
                                {users.status === "done" &&
                                    !Boolean(users.data.length) &&
                                    isEqual(filters, filtersInitialValue) && (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Box className={classes.noDataWrapper}>
                                                    <img
                                                        width={400}
                                                        src={drax_lighthouse_animation}
                                                        alt="Results Not Found"
                                                    />
                                                    <Typography>We couldn’t find any matches.</Typography>
                                                    <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                                                        Please double check your search or try again.
                                                    </Typography>
                                                    <Box display="flex" alignItems="center">
                                                        <NavButton
                                                            variant="outlined"
                                                            showBackArrow={false}
                                                            onClick={() => console.log("clicked")}
                                                            sx={{ mr: 1 }}
                                                            startIcon={<CustomIcon icon={icons.close} />}
                                                        >
                                                            Clear Search
                                                        </NavButton>
                                                        <NavButton
                                                            showBackArrow={false}
                                                            onClick={handleOpenInvitationModal}
                                                            startIcon={<CustomIcon icon={icons.plus} />}
                                                        >
                                                            Invite New User
                                                        </NavButton>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                {/* No match */}
                                {users.status === "done" &&
                                    !Boolean(users.data.length) &&
                                    !isEqual(filters, filtersInitialValue) && (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Box className={classes.noDataWrapper}>
                                                    <Typography>
                                                        <FormattedMessage
                                                            id="hubManagement.client.usersTable.noMatchesMessage1"
                                                            values={{ string: filters.filter_name }}
                                                        />
                                                    </Typography>
                                                    <Typography>
                                                        <FormattedMessage id="hubManagement.client.usersTable.noMatchesMessage2" />
                                                    </Typography>
                                                    <Box display="flex">
                                                        <NavButton
                                                            showBackArrow={false}
                                                            variant="outlined"
                                                            onClick={() => handleClearFilters("all")}
                                                            startIcon={
                                                                <CustomIcon icon={icons.close} sx={{ fontSize: 14 }} />
                                                            }
                                                            translationId="hubManagement.client.button.clearSearch"
                                                            sx={{ marginRight: 2 }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                {canInviteUsers && (
                    <InviteUsersModal
                        open={inviteModalOpen}
                        activeTab={activeTab}
                        onClose={closeInviteModal}
                        pendingInvitations={pendingInvitations}
                        org_id={orgId}
                        fetchUsersData={() =>
                            handleFetchData({
                                ...filters,
                                order_by: `${usersSort.columnId},${usersSort.direction}`,
                                page_no: Number(inputPage),
                            })
                        }
                    />
                )}
            </Body>
        </>
    );
};

export default AssessmentCentre;
